/**
 * Deterministic approver import (Time Approval Routing, Part 2 — AI import).
 *
 * The ONLY AI step is normalizing pasted free text into structured
 * { wbsRef, approverRef } pairs (done in the service via generateWithClaude).
 * Everything here is deterministic and pure: parse that JSON defensively, then
 * map each wbsRef to a real charge object and each approverRef to a real user
 * (by id, else exact name match; ambiguous / not-found rows are flagged, never
 * guessed). No AI in the mapping step. UI-free / network-free → testable.
 */
import { type SapObjectType, normalizeWbsKey } from './sapChargeObjects'

export interface ParsedApproverPair {
  wbsRef: string
  approverRef: string
}

export interface ImportObject {
  id: string
  objectKey: string
  sapObjectType: SapObjectType
  attributes: Record<string, unknown> | null
}

export interface ImportUser {
  id: string
  name: string
}

export interface ResolvedImportRow {
  wbsRef: string
  approverRef: string
  objectId: string
  objectKey: string
  sapObjectType: SapObjectType
  approverUserId: string
  approverName: string | null
}

export interface UnresolvedImportRow {
  wbsRef: string
  approverRef: string
  reason: string
}

export interface ApproverImportPlan {
  resolved: ResolvedImportRow[]
  unresolved: UnresolvedImportRow[]
}

/**
 * Parse the AI's JSON into structured pairs, defensively (mirrors
 * parseSapPullPlan): accepts a bare array, or an object with a `pairs` /
 * `mappings` array, and tolerates a markdown-fenced or prose-wrapped blob.
 */
export function parseApproverPairs(text: string): ParsedApproverPair[] {
  if (!text || !text.trim()) return []
  let raw: unknown
  try { raw = JSON.parse(text) } catch {
    const m = text.match(/\[[\s\S]*\]|\{[\s\S]*\}/)
    if (!m) return []
    try { raw = JSON.parse(m[0]) } catch { return [] }
  }
  const arr: unknown = Array.isArray(raw)
    ? raw
    : (raw && typeof raw === 'object')
      ? ((raw as Record<string, unknown>).pairs ?? (raw as Record<string, unknown>).mappings ?? [])
      : []
  if (!Array.isArray(arr)) return []
  const out: ParsedApproverPair[] = []
  for (const e of arr) {
    if (!e || typeof e !== 'object') continue
    const o = e as Record<string, unknown>
    const wbsRef = typeof o.wbsRef === 'string' ? o.wbsRef.trim()
      : typeof o.wbs === 'string' ? o.wbs.trim() : ''
    const approverRef = typeof o.approverRef === 'string' ? o.approverRef.trim()
      : typeof o.approver === 'string' ? o.approver.trim() : ''
    if (wbsRef && approverRef) out.push({ wbsRef, approverRef })
  }
  return out
}

function norm(s: unknown): string {
  return normalizeWbsKey(String(s ?? '').trim()).toLowerCase()
}

/** Object lookup keys: object key + external / internal WBS attributes. */
function objectKeys(o: ImportObject): string[] {
  const a = o.attributes ?? {}
  const raw = [o.objectKey, a.WbsElementExternal, a.WbsElement, a.WBSElement]
  const keys = new Set<string>()
  for (const r of raw) { const k = norm(r); if (k) keys.add(k) }
  return [...keys]
}

/**
 * Resolve parsed pairs against the real charge objects + users. wbsRef matches a
 * charge object by normalized key; approverRef matches a user by id (exact), else
 * by exact name (case-insensitive). Multiple name matches => ambiguous (flagged).
 */
export function resolveApproverImport(
  pairs: ReadonlyArray<ParsedApproverPair>,
  objects: ReadonlyArray<ImportObject>,
  users: ReadonlyArray<ImportUser>,
): ApproverImportPlan {
  const objectByKey = new Map<string, ImportObject>()
  for (const o of objects) for (const k of objectKeys(o)) if (!objectByKey.has(k)) objectByKey.set(k, o)

  const userById = new Map<string, ImportUser>()
  for (const u of users) userById.set(u.id.trim().toLowerCase(), u)
  // name -> user ids (a name can be non-unique)
  const userIdsByName = new Map<string, string[]>()
  for (const u of users) {
    const n = u.name.trim().toLowerCase()
    if (!n) continue
    const list = userIdsByName.get(n) ?? []
    list.push(u.id); userIdsByName.set(n, list)
  }

  const resolved: ResolvedImportRow[] = []
  const unresolved: UnresolvedImportRow[] = []

  for (const p of pairs) {
    const obj = objectByKey.get(norm(p.wbsRef))
    if (!obj) {
      unresolved.push({ wbsRef: p.wbsRef, approverRef: p.approverRef, reason: 'WBS / charge object not found in the catalog' })
      continue
    }
    const ref = p.approverRef.trim()
    const byId = userById.get(ref.toLowerCase())
    let user: ImportUser | undefined = byId
    if (!user) {
      const ids = userIdsByName.get(ref.toLowerCase()) ?? []
      if (ids.length > 1) {
        unresolved.push({ wbsRef: p.wbsRef, approverRef: p.approverRef, reason: `Approver name matches ${ids.length} users (ambiguous)` })
        continue
      }
      if (ids.length === 1) user = users.find((u) => u.id === ids[0])
    }
    if (!user) {
      unresolved.push({ wbsRef: p.wbsRef, approverRef: p.approverRef, reason: 'Approver not found (no matching user id or name)' })
      continue
    }
    resolved.push({
      wbsRef: p.wbsRef,
      approverRef: p.approverRef,
      objectId: obj.id,
      objectKey: obj.objectKey,
      sapObjectType: obj.sapObjectType,
      approverUserId: user.id,
      approverName: user.name,
    })
  }

  return { resolved, unresolved }
}
